/**
 * Document size as a coarse bucket for analytics (`doc_size`).
 *
 * Asked for on 2026-10-05 while weighing a Pro plan for very long documents:
 * before choosing where a free limit would sit, we need to know how many
 * exports and imports land at each size. `content_analyzed` has carried an
 * exact `word_count` since July, but it is not a registered GA4 dimension
 * (and a raw number would not chart as a distribution anyway); the only
 * registered size field, `content_complexity`, stops at "over 1,000 words".
 *
 * Buckets are in words — about 500 to an A4 page at the default setup, so
 * 5k ≈ 10 pages, 20k ≈ 40, 50k ≈ 100, 100k ≈ 200. Words, not characters:
 * an inlined image is one word but thousands of base64 characters, and LaTeX
 * source inflates characters without adding pages. Known blind spot: text
 * without spaces (Chinese, Japanese) counts each run as one word.
 */

export type DocSize = "under_1k" | "1k_5k" | "5k_20k" | "20k_50k" | "50k_100k" | "100k_plus";

const BUCKETS: [limit: number, size: DocSize][] = [
  [1_000, "under_1k"],
  [5_000, "1k_5k"],
  [20_000, "5k_20k"],
  [50_000, "20k_50k"],
  [100_000, "50k_100k"],
];

export function docSizeBucket(words: number): DocSize {
  for (const [limit, size] of BUCKETS) if (words < limit) return size;
  return "100k_plus";
}

/**
 * Whitespace-separated runs, exactly as `text.split(/\s+/)` with empties
 * dropped would count them — without building an array of every word for a
 * 2 M-character document.
 */
export function countWords(text: string): number {
  let words = 0;
  let inWord = false;
  for (let i = 0; i < text.length; i++) {
    if (isSpace(text.charCodeAt(i))) inWord = false;
    else if (!inWord) {
      inWord = true;
      words++;
    }
  }
  return words;
}

/** The characters JavaScript's `\s` matches. */
function isSpace(c: number): boolean {
  return (
    c === 0x20 ||
    (c >= 0x09 && c <= 0x0d) ||
    c === 0xa0 ||
    c === 0x1680 ||
    (c >= 0x2000 && c <= 0x200a) ||
    c === 0x2028 ||
    c === 0x2029 ||
    c === 0x202f ||
    c === 0x205f ||
    c === 0x3000 ||
    c === 0xfeff
  );
}
